/**
 * Theme payload validator — Surface Layer (/01)
 *
 * Filters arbitrary token payloads down to only allowed keys with
 * shape-valid values. Rejected keys are returned for observability so
 * the provider can emit THEME_REJECTED events.
 */

import {
  ALLOWED_CARBON_TOKEN_KEYS,
  ALLOWED_SURFACE_TOKEN_KEYS,
  type SurfaceTokenOverrides,
  type ThemeTokenOverrides,
} from '@contracts/theme.contract';
import {
  isColorKey,
  isFontKey,
  isLengthKey,
  isLikelyCssColor,
  isSafeTokenValue,
  parseLengthToPx,
} from './theme-tokens';

interface ValidatedTokens {
  accepted: ThemeTokenOverrides;
  surfaceAccepted: SurfaceTokenOverrides;
  rejected: Array<{ key: string; reason: string }>;
}

const RADIUS_MIN_PX = 0;
const RADIUS_MAX_PX = 24;
const CONTAINER_MIN_PX = 320;
const CONTAINER_MAX_PX = 2560;

export function validateThemePayload(
  raw:
    | {
        tokens?: Partial<Record<string, unknown>> | ThemeTokenOverrides;
        surfaceTokens?: Partial<Record<string, unknown>> | SurfaceTokenOverrides;
      }
    | undefined
): ValidatedTokens {
  const result: ValidatedTokens = {
    accepted: {},
    surfaceAccepted: {},
    rejected: [],
  };
  if (!raw) return result;

  const tokens = raw.tokens ?? {};
  for (const [key, value] of Object.entries(tokens)) {
    if (typeof value !== 'string') {
      result.rejected.push({ key, reason: 'value is not a string' });
      continue;
    }
    if (!isSafeTokenValue(value)) {
      result.rejected.push({ key, reason: 'value contains forbidden syntax (javascript:, url(), <)' });
      continue;
    }
    if (!ALLOWED_CARBON_TOKEN_KEYS.includes(key as keyof ThemeTokenOverrides)) {
      result.rejected.push({ key, reason: 'key not in allow-list' });
      continue;
    }
    if (isLengthKey(key)) {
      const px = parseLengthToPx(value);
      if (px === null) {
        result.rejected.push({ key, reason: 'value is not a recognized length (px or rem)' });
        continue;
      }
      if (key.endsWith('border-radius')) {
        if (px < RADIUS_MIN_PX || px > RADIUS_MAX_PX) {
          result.rejected.push({ key, reason: `radius out of range ${RADIUS_MIN_PX}–${RADIUS_MAX_PX}px` });
          continue;
        }
      }
      result.accepted[key as keyof ThemeTokenOverrides] = value;
      continue;
    }
    if (isColorKey(key)) {
      if (!isLikelyCssColor(value)) {
        result.rejected.push({ key, reason: 'value is not a recognized CSS color' });
        continue;
      }
      result.accepted[key as keyof ThemeTokenOverrides] = value;
      continue;
    }
    if (isFontKey(key)) {
      // Accept any non-empty string here; the SCSS load will catch unknown families.
      if (value.trim().length === 0) {
        result.rejected.push({ key, reason: 'font-family is empty' });
        continue;
      }
      result.accepted[key as keyof ThemeTokenOverrides] = value;
      continue;
    }
    result.rejected.push({ key, reason: 'unhandled key category' });
  }

  const surfaceTokens = raw.surfaceTokens ?? {};
  for (const [key, value] of Object.entries(surfaceTokens)) {
    if (!ALLOWED_SURFACE_TOKEN_KEYS.includes(key as keyof SurfaceTokenOverrides)) {
      result.rejected.push({ key, reason: 'surface key not in allow-list' });
      continue;
    }
    if (typeof value !== 'string') {
      result.rejected.push({ key, reason: 'value is not a string' });
      continue;
    }
    if (!isSafeTokenValue(value)) {
      result.rejected.push({ key, reason: 'value contains forbidden syntax' });
      continue;
    }
    if (key === 'surface-container-max-width') {
      const px = parseLengthToPx(value);
      if (px === null || px < CONTAINER_MIN_PX || px > CONTAINER_MAX_PX) {
        result.rejected.push({ key, reason: `max-width out of range ${CONTAINER_MIN_PX}–${CONTAINER_MAX_PX}px` });
        continue;
      }
      result.surfaceAccepted[key] = value;
      continue;
    }
    if (key === 'surface-density') {
      if (!['compact', 'comfortable', 'spacious'].includes(value)) {
        result.rejected.push({ key, reason: 'density must be compact|comfortable|spacious' });
        continue;
      }
      result.surfaceAccepted[key] = value as 'compact' | 'comfortable' | 'spacious';
      continue;
    }
    result.rejected.push({ key, reason: 'unhandled surface key' });
  }

  return result;
}
